import { useRef, useState, useEffect } from 'react';
import { PLACEHOLDER_ICONS } from '../api';
import { FlameIcon } from '../icons';
import { cardDisplayType, cardRedRemaining } from '../lib/cardUtils';
import Tooltip from './Tooltip';

function useInView() {
	const ref = useRef(null);
	const [inView, setInView] = useState(false);

	useEffect(() => {
		const el = ref.current;
		if (!el) return undefined;
		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry.isIntersecting) {
					setInView(true);
					observer.disconnect();
				}
			},
			{ threshold: 0 }
		);
		observer.observe(el);
		return () => observer.disconnect();
	}, []);

	return [ref, inView];
}

function useCountUp(target, active, duration = 1500, popEarly = 400) {
	const [value, setValue] = useState(0);
	const [finished, setFinished] = useState(false);

	useEffect(() => {
		if (!active || target <= 0) return undefined;
		let raf;
		const start = performance.now();
		const popAt = 1 - popEarly / duration;
		const tick = (now) => {
			const progress = Math.min(1, (now - start) / duration);
			const eased = 1 - Math.pow(1 - progress, 3);
			setValue(Math.round(target * eased));
			if (progress >= popAt) {
				setFinished(true);
			}
			if (progress < 1) {
				raf = requestAnimationFrame(tick);
			}
		};
		raf = requestAnimationFrame(tick);
		return () => cancelAnimationFrame(raf);
	}, [active, target, duration, popEarly]);

	return [value, finished];
}

function formatDays(days) {
	return days === 1 ? '1 Tag' : `${days} Tage`;
}

function CardBadge({ card, color }) {
	return (
		<span className={`fairplay-rules-standings__card fairplay-rules-standings__card--${color}`}>
			{card.remaining}T
		</span>
	);
}

function PlayerRow({ player }) {
	const [ref, inView] = useInView();
	const [streak] = useCountUp(player.streak, inView);
	const [animationPhase, setAnimationPhase] = useState(0);
	const phaseStarted = useRef(false);

	const now = new Date();
	const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(
		now.getDate()
	).padStart(2, '0')}`;
	const yellow = (player.active_cards || []).filter((c) => cardDisplayType(c, today) === 'yellow');
	const red = (player.active_cards || []).filter((c) => cardDisplayType(c, today) === 'red');
	const gifts = player.gifts || [];

	const flameTier =
		player.streak >= 16
			? { name: 'blazing', duration: '0.5s' }
			: player.streak >= 12
				? { name: 'hot', duration: '0.7s' }
				: player.streak >= 8
					? { name: 'warm', duration: '0.9s' }
					: player.streak >= 4
						? { name: 'ember', duration: '1.2s' }
						: { name: 'cold', duration: '1.6s' };

	const gradientPct = Math.min(100, Math.max(0, (player.score / 16) * 100));
	const overlayPct = player.score > 16
		? Math.min(100, (Math.log(player.score - 15) / Math.log(85)) * 100)
		: 0;
	const activeEdgePct = overlayPct > 0 ? overlayPct : gradientPct;
	const edgeVariant = player.score > 16 ? 'high' : player.score > 8 ? 'mid' : 'low';

	useEffect(() => {
		if (!inView || phaseStarted.current) return undefined;
		phaseStarted.current = true;
		setAnimationPhase(1);
		if (overlayPct > 0) {
			const timer = setTimeout(() => setAnimationPhase(2), 1200);
			return () => clearTimeout(timer);
		}
		return undefined;
	}, [inView, overlayPct]);

	return (
		<tr ref={ref}>
			<td className="fairplay-rules-standings__rank">
				<div className="fairplay-rules-standings__rank-inner">
					<span className="fairplay-rules-standings__rank-number">
						{player.rank}
					</span>
					{player.avatar ? (
						<img
							className="fairplay-rules-avatar fairplay-rules-standings__rank-avatar"
							src={player.avatar}
							alt=""
						/>
					) : (
						<span className="fairplay-rules-standings__rank-avatar fairplay-rules-standings__rank-avatar--placeholder" />
					)}
				</div>
			</td>
			<td className="fairplay-rules-standings__player">
				<div className="fairplay-rules-standings__player-inner">
					<span className="fairplay-rules-standings__level">LvL {player.level}</span>
					<Tooltip title={`${player.score} XP`} panelStyle={{ left: `${activeEdgePct}%` }}>
						<div className="fairplay-rules-standings__scorebar">
							<span className="fairplay-rules-standings__scorebar-tick" style={{ left: '25%' }} />
							<span className="fairplay-rules-standings__scorebar-tick" style={{ left: '50%' }} />
							<span className="fairplay-rules-standings__scorebar-tick" style={{ left: '75%' }} />
							<div
								className="fairplay-rules-standings__scorebar-gradient"
								style={{
									clipPath: `inset(0 ${animationPhase >= 1 ? 100 - gradientPct : 100}% 0 0)`,
									transition: 'clip-path 1.2s ease',
								}}
							>
								<div className="fairplay-rules-standings__scorebar-gradient-core" />
							</div>
							<div
								className="fairplay-rules-standings__scorebar-overlay"
								style={{
									width: `${animationPhase >= 2 ? overlayPct : 0}%`,
									transition: animationPhase >= 2 ? 'width 1.2s ease' : 'none',
								}}
							>
								<div className="fairplay-rules-standings__scorebar-overlay-core" />
							</div>
						{animationPhase < 2 && (
							<div
								className={`fairplay-rules-standings__scorebar-edge fairplay-rules-standings__scorebar-edge--${edgeVariant}`}
								style={{
									left: `${animationPhase >= 1 ? gradientPct : 0}%`,
									transition: 'left 1.2s ease',
								}}
							/>
						)}
						{overlayPct > 0 && (
							<div
								className={`fairplay-rules-standings__scorebar-edge fairplay-rules-standings__scorebar-edge--${edgeVariant}`}
								style={{
									left: `${animationPhase >= 2 ? overlayPct : 0}%`,
									opacity: animationPhase >= 2 ? 1 : 0,
									transition: animationPhase >= 2 ? 'left 1.2s ease, opacity 0.2s ease' : 'none',
								}}
							/>
						)}
						</div>
					</Tooltip>
					<span className="fairplay-rules-standings__name">{player.name}</span>
					{(player.trophies || []).length > 0 || gifts.length > 0 || yellow.length > 0 || red.length > 0 ? (
						<div className="fairplay-rules-standings__meta-row">
							{(player.trophies || []).length > 0 && (
								<div className="fairplay-rules-standings__trophies">
									{(player.trophies || []).map((t) => (
										<Tooltip key={t.id} title={t.name} description={t.description} position="right">
											<img
												className="fairplay-rules-standings__trophy"
												src={t.icon || PLACEHOLDER_ICONS.trophy || ''}
												alt=""
											/>
										</Tooltip>
									))}
								</div>
							)}
							{(yellow.length > 0 || red.length > 0 || gifts.length > 0) && (
								<div className="fairplay-rules-standings__rewards-right">
									{gifts.length > 0 && (
										<div className="fairplay-rules-standings__gifts">
											{gifts.map((g) => (
										<Tooltip key={g.id} title={g.name} description={g.description} position="left">
											<img
												className="fairplay-rules-standings__gift"
												src={g.icon || PLACEHOLDER_ICONS.gift || ''}
												alt=""
											/>
										</Tooltip>
									))}
										</div>
									)}
									{(yellow.length > 0 || red.length > 0) && (
										<div className={`fairplay-rules-standings__cards${inView ? ' is-visible' : ''}`}>
											{yellow.map((c) => (
												<Tooltip
													key={c.id}
													title="Gelbe Karte"
													description={"Noch " + formatDays(c.remaining)}
													position="left"
												>
													<CardBadge card={c} color="yellow" />
												</Tooltip>
											))}
											{red.map((c) => {
												const redRemaining = cardRedRemaining(c);
												return (
													<Tooltip
														key={c.id}
														title="Rote Karte"
														description={"Noch " + formatDays(redRemaining)}
														position="left"
													>
														<CardBadge card={{ ...c, remaining: redRemaining }} color="red" />
													</Tooltip>
												);
											})}
										</div>
									)}
								</div>
							)}
						</div>
					) : null}
				</div>
			</td>
			<td className="fairplay-rules-standings__streak">
				<div className="fairplay-rules-standings__streak-inner">
					<span
						className={`streak-flame streak-flame--${flameTier.name}${inView ? ' is-lit' : ''} fairplay-rules-streak-hover-trigger`}
					>
						<FlameIcon
							className="streak-flame__svg"
							style={{ animationDuration: flameTier.duration }}
						/>
					</span>
					<Tooltip title="Streak" description={formatDays(streak)} position="left">
						<span className="fairplay-rules-standings__level">{streak}</span>
					</Tooltip>
				</div>
			</td>
		</tr>
	);
}

export default function PlayerStandings({ standings, loading, error, onRetry }) {
	if (loading) {
		return (
			<section className="fairplay-rules-section">
				<div className="fairplay-rules-loading">Spieler werden geladen...</div>
			</section>
		);
	}

	if (error) {
		return (
			<div className="fairplay-rules-notice fairplay-rules-notice--error">
				<p>{error}</p>
				<button className="button" onClick={onRetry}>
					Erneut versuchen
				</button>
			</div>
		);
	}

	const sorted = [...standings]
		.filter((entry) => entry.has_season_card)
		.sort((a, b) => b.score - a.score || b.streak - a.streak);

	const ranked = [];
	let groupStart = 0;
	for (let i = 0; i < sorted.length; i++) {
		const next = sorted[i + 1];
		if (!next || next.score !== sorted[i].score) {
			const label = groupStart + 1 === i + 1 ? `#${i + 1}` : `#${groupStart + 1}-${i + 1}`;
			for (let j = groupStart; j <= i; j++) {
				ranked.push({ ...sorted[j], rank: label });
			}
			groupStart = i + 1;
		}
	}

	return (
		<section className="fairplay-rules-section">
			{sorted.length === 0 ? (
				<div className="fairplay-rules-empty">Noch keine Spieler vorhanden.</div>
			) : (
				<table className="fairplay-rules-standings">
					<tbody>
						{ranked.map((player) => (
							<PlayerRow
								key={player.id}
								player={player}
							/>
						))}
					</tbody>
				</table>
			)}
		</section>
	);
}
